import dayjs from 'dayjs';
import { IArtistProfile } from 'hooks/swrRequests/useArtistProfiles';
import { COUNTRY_NAMES, countryCodeByName } from 'constants/iso-countries';
import { requiresState, SUBDIVISIONS } from 'constants/iso-subdivisions';

/**
 * Declarative config of the artist onboarding steps (Personal → Contact →
 * Advance & Royalties → Marketing → Payouts → Review), mirroring the
 * SongFund Onboarding design.
 * Labels/intros are literal English i18n keys — render through t().
 * `missingFields` from the server is the single source of truth for
 * step completeness and overall progress.
 */

export type ProfileScalarKey =
  | 'fullName'
  | 'artistName'
  | 'dateOfBirth'
  | 'nationality'
  | 'phone'
  | 'secondaryPhone'
  | 'secondaryEmail'
  | 'location'
  | 'residentialAddress'
  | 'residentialAddress2'
  | 'city'
  | 'state'
  | 'postalCode'
  | 'country'
  | 'countryCode'
  | 'welcomeGift'
  | 'welcomeGiftAddressType'
  | 'giftAddress'
  | 'giftAddress2'
  | 'giftCity'
  | 'giftState'
  | 'giftPostalCode'
  | 'giftCountry'
  | 'giftCountryCode'
  | 'taxNumber'
  | 'spotifyUrl'
  | 'instagram'
  | 'tiktok'
  | 'curveDownloaded'
  | 'recoupmentEmail'
  | 'goals'
  | 'contactEmail'
  | 'contactPhone'
  | 'genre'
  | 'techPlatformSignup'
  | 'collabInterest'
  | 'marketingCall'
  | 'marketingActivations'
  | 'bankAccountName'
  | 'bankAccountAddress'
  | 'bankName'
  | 'bankAccountNumber'
  | 'iban'
  | 'bankRouting'
  | 'swift'
  | 'paymentEmail';

export type ProfileFieldKey = ProfileScalarKey | 'accountEmail';

/** The saved profile or the step form's live values — what conditional fields are evaluated against. */
export type ProfileFormValues = Partial<Record<ProfileScalarKey, string | null | undefined>>;

/** A select option: the stored value, or value + display label (label is an i18n key). */
export type FieldOption = string | { value: string; label: string };

/** Mirror of the backend's YES_NO_OPTIONS — values are stored/synced verbatim, so keep in sync. */
export const YES_NO_OPTIONS = ['Yes', 'No'];

export interface ProfileStepField {
  key: ProfileFieldKey;
  label: string;
  required?: boolean;
  placeholder?: string;
  date?: boolean;
  /** Fixed-option select (backend rejects other values); display text is translated, value isn't. */
  options?: FieldOption[] | ((values: ProfileFormValues) => FieldOption[]);
  /** Selects are compact by default (Yes/No); set for long option lists. */
  fullWidth?: boolean;
  /** Autocomplete suggestions — free text unless `strict`. */
  suggestions?: string[];
  /** Only a suggestion may be picked (backend rejects anything else). */
  strict?: boolean;
  /**
   * Rendered, counted and reviewed only while this holds for the form/profile values.
   * Conditional fields stay out of the static required total (see getProfileProgress).
   */
  showWhen?: (values: ProfileFormValues) => boolean;
  /** Multi-line free text. */
  multiline?: boolean;
  /** Instructional text above the field; `<a>…</a>` marks the anchor (rendered via Trans — don't use `<link>`, it's an HTML void tag the parser self-closes). */
  note?: { text: string; href: string };
  /** Secondary hint shown below the label. */
  hint?: string;
  /** HTML autofill token for the input's autocomplete attribute (e.g. 'name', 'tel', 'email'). */
  autoComplete?: string;
  /** When the profile has no value yet, prefill the input from this profile field. */
  prefillFrom?: ProfileScalarKey;
  /** Spotify artist search autocomplete; selecting an artist stores their profile URL. */
  spotifySearch?: boolean;
  /** Shown as a read-only value row; never sent in the PATCH. */
  readOnly?: boolean;
  /** Value lives on the user account (not the artist profile) and doesn't count toward completion. */
  accountScope?: boolean;
  /** Banking detail — masked to the last 4 characters in the review tab. */
  sensitive?: boolean;
}

export interface ProfileStep {
  id: 'about' | 'contact' | 'advance' | 'marketing' | 'payouts' | 'review';
  label: string;
  /** Title of the card the step's fields render in (defaults to the label); also titles the review group. */
  section?: string;
  intro?: string;
  fields?: ProfileStepField[];
  /** Show the banking-proof upload at the bottom of the step. */
  withUpload?: boolean;
  /** Show the welcome gift opt-in (with its optional alternate address) at the bottom of the step. */
  withWelcomeGift?: boolean;
  isReview?: boolean;
}

/** Mirror of the backend's GIFT_ADDRESS_TYPES — stored/synced verbatim and doubling as the radio labels (i18n keys). */
export const GIFT_ADDRESS_RESIDENTIAL = 'My residential address above';
export const GIFT_ADDRESS_OTHER = 'A different address';

export const shipsGiftToOtherAddress = (
  profile: Pick<ProfileFormValues, 'welcomeGift' | 'welcomeGiftAddressType'>
): boolean =>
  profile.welcomeGift === 'Yes' && profile.welcomeGiftAddressType === GIFT_ADDRESS_OTHER;

/** Mirror of the backend's TAX_NUMBER_REQUIRED_COUNTRIES (Printful: CPF/CNPJ for Brazil). */
const TAX_NUMBER_REQUIRED_COUNTRIES = ['BR'];

/** Shipping needs a state/province (US, AU, CA, BR, JP) for the country typed in `key`. */
const needsState = (values: ProfileFormValues, key: 'country' | 'giftCountry') =>
  requiresState(countryCodeByName(values[key]));

const subdivisionOptions =
  (key: 'country' | 'giftCountry') =>
  (values: ProfileFormValues): FieldOption[] => {
    const code = countryCodeByName(values[key]);
    return requiresState(code)
      ? SUBDIVISIONS[code].map(({ code: value, name }) => ({ value, label: name }))
      : [];
  };

/** The gift ships to a country whose customs need the recipient's tax id. */
export const needsTaxNumber = (values: ProfileFormValues): boolean => {
  if (values.welcomeGift !== 'Yes') return false;
  const country = shipsGiftToOtherAddress(values) ? values.giftCountry : values.country;
  const code = countryCodeByName(country);
  return !!code && TAX_NUMBER_REQUIRED_COUNTRIES.includes(code);
};

export const isFieldVisible = (field: ProfileStepField, values: ProfileFormValues): boolean =>
  !field.showWhen || field.showWhen(values);

/**
 * Alternate shipping address for the welcome gift. Kept out of PROFILE_STEPS:
 * required only while the gift ships to it, so it isn't part of the static total.
 */
export const GIFT_ADDRESS_FIELDS: ProfileStepField[] = [
  {
    key: 'giftAddress',
    label: 'Gift address',
    required: true,
    autoComplete: 'street-address',
    showWhen: shipsGiftToOtherAddress,
  },
  {
    key: 'giftAddress2',
    label: 'Gift address line 2',
    autoComplete: 'address-line2',
    showWhen: shipsGiftToOtherAddress,
  },
  {
    key: 'giftCity',
    label: 'Gift city',
    required: true,
    autoComplete: 'address-level2',
    showWhen: shipsGiftToOtherAddress,
  },
  {
    key: 'giftPostalCode',
    label: 'Gift postal code',
    required: true,
    autoComplete: 'postal-code',
    showWhen: shipsGiftToOtherAddress,
  },
  {
    key: 'giftCountry',
    label: 'Gift country',
    required: true,
    suggestions: COUNTRY_NAMES,
    strict: true,
    showWhen: shipsGiftToOtherAddress,
  },
  {
    key: 'giftState',
    label: 'Gift state / province',
    required: true,
    options: subdivisionOptions('giftCountry'),
    fullWidth: true,
    showWhen: (values) => shipsGiftToOtherAddress(values) && needsState(values, 'giftCountry'),
  },
];

/** Recipient tax id, asked only while the gift ships to a country that needs it. */
export const TAX_NUMBER_FIELD: ProfileStepField = {
  key: 'taxNumber',
  label: 'CPF / CNPJ',
  hint: 'Required by customs for deliveries to Brazil',
  placeholder: '000.000.000-00',
  required: true,
  showWhen: needsTaxNumber,
};

const WELCOME_GIFT_FIELDS: ProfileStepField[] = [
  { key: 'welcomeGift', label: 'Welcome gift' },
  { key: 'welcomeGiftAddressType', label: 'Gift address type' },
  ...GIFT_ADDRESS_FIELDS,
  TAX_NUMBER_FIELD,
];

/** Every field the step's form holds and saves (config fields + the welcome gift block). */
export const getEditableFields = (step: ProfileStep): ProfileStepField[] => [
  ...(step.fields || []).filter((field) => !field.readOnly),
  ...(step.withWelcomeGift ? WELCOME_GIFT_FIELDS : []),
];

export const PROFILE_STEPS: ProfileStep[] = [
  {
    id: 'about',
    label: 'Personal',
    section: 'Personal Details',
    intro: 'Who the artist is — we verify this for KYC.',
    fields: [
      { key: 'artistName', label: 'Artist name', required: true, readOnly: true },
      {
        key: 'fullName',
        label: 'Full Name (as it appears on ID)',
        required: true,
        placeholder: "Artist's legal name",
        autoComplete: 'name',
      },
      {
        key: 'dateOfBirth',
        label: 'Date of birth',
        required: true,
        date: true,
        autoComplete: 'bday',
      },
      {
        key: 'nationality',
        label: 'Nationality',
        required: true,
        suggestions: COUNTRY_NAMES,
        strict: true,
      },
    ],
  },
  {
    id: 'contact',
    label: 'Contact',
    section: 'Contact Details',
    intro: 'How we reach the artist.',
    withWelcomeGift: true,
    fields: [
      {
        key: 'accountEmail',
        label: 'Primary Email Address',
        required: true,
        readOnly: true,
        accountScope: true,
      },
      { key: 'secondaryEmail', label: 'Secondary Email Address', autoComplete: 'email' },
      { key: 'phone', label: 'Primary Phone Number', required: true, autoComplete: 'tel' },
      { key: 'secondaryPhone', label: 'Secondary Phone Number', autoComplete: 'tel' },
      {
        key: 'residentialAddress',
        label: 'Residential address',
        required: true,
        autoComplete: 'street-address',
      },
      { key: 'residentialAddress2', label: 'Address line 2', autoComplete: 'address-line2' },
      { key: 'city', label: 'City', required: true, autoComplete: 'address-level2' },
      { key: 'postalCode', label: 'Postal code', required: true, autoComplete: 'postal-code' },
      {
        key: 'country',
        label: 'Country',
        required: true,
        suggestions: COUNTRY_NAMES,
        strict: true,
      },
      {
        key: 'state',
        label: 'State / Province',
        required: true,
        options: subdivisionOptions('country'),
        fullWidth: true,
        showWhen: (values) => needsState(values, 'country'),
      },
    ],
  },
  {
    id: 'advance',
    label: 'Advance & Royalties',
    // intro: 'Your Curve account for advances and royalty statements.',
    fields: [
      {
        key: 'curveDownloaded',
        label: 'Have you downloaded your Snafu Records Curve account guide?',
        required: true,
        options: YES_NO_OPTIONS,
        note: {
          text: 'Please download your Snafu Records Curve account guide <a>here</a> before submitting the form.',
          href: 'https://drive.google.com/file/d/1OBUK4UfJo-fFzfYpYgiUbuDgbAwg4-B-/view?usp=sharing',
        },
      },
      {
        key: 'recoupmentEmail',
        label: 'Which e-mail address would you like to login with to check your recoupment status?',
        required: true,
        autoComplete: 'email',
      },
    ],
  },
  {
    id: 'marketing',
    label: 'Marketing',
    // intro: 'Helps our marketing team plan and support your releases.',
    section: 'Marketing Information',
    fields: [
      {
        key: 'goals',
        label:
          'Overall, what are your high level goals with us? What are your plans to spend your advance? How much have you reserved from the advance for marketing?',
        multiline: true,
      },
      {
        key: 'contactEmail',
        label: 'What is the best email to contact you?',
        required: true,
        autoComplete: 'email',
      },
      {
        key: 'contactPhone',
        label: 'What is the best phone number to contact you?',
        required: true,
        autoComplete: 'tel',
        prefillFrom: 'phone',
      },
      {
        key: 'genre',
        label: 'What genre would you self classify as?',
        required: true,
      },
      {
        key: 'location',
        label: 'Where are you located (City, Country)?',
        required: true,
      },
      {
        key: 'collabInterest',
        label: 'Are you interested in collaboration opportunities?',
        required: true,
        options: YES_NO_OPTIONS,
      },
      {
        key: 'marketingCall',
        label: 'Have you had a call with our marketing team yet?',
        options: YES_NO_OPTIONS,
      },
      {
        key: 'marketingActivations',
        label: 'Are there any other marketing activations you are particularly curious about?',
        multiline: true,
      },
      {
        key: 'spotifyUrl',
        label: 'Spotify URL',
        required: true,
        placeholder: 'Search artist or enter Spotify URL',
        spotifySearch: true,
      },
      { key: 'instagram', label: 'Instagram' },
      { key: 'tiktok', label: 'TikTok' },
    ],
  },
  {
    id: 'payouts',
    label: 'Payouts',
    section: 'Bank Details',
    intro: 'Where the advance and royalties get sent. May differ from the artist name.',
    withUpload: true,
    fields: [
      {
        key: 'paymentEmail',
        label: 'Email account for payment confirmation',
        required: true,
        readOnly: true,
      },
      {
        key: 'bankAccountName',
        label: 'Name as per bank records',
        required: true,
        autoComplete: 'name',
        hint: "Account holder's name as per the bank account.",
      },
      {
        key: 'bankAccountAddress',
        label: 'Address',
        hint: 'Account holder address & country',
        required: true,
        autoComplete: 'street-address',
      },
      { key: 'bankAccountNumber', label: 'Account number', required: true, sensitive: true },
      { key: 'iban', label: 'IBAN', hint: 'If applicable.', sensitive: true },
      {
        key: 'bankRouting',
        label: 'ABA / Routing / Sort code',
        hint: 'If applicable.',
        sensitive: true,
      },
      { key: 'swift', label: 'BIC / SWIFT', required: true, sensitive: true },
      {
        key: 'bankName',
        label: 'Bank name & address',
        required: true,
        hint: 'Please also include postcode, city and country.',
      },
    ],
  },
  { id: 'review', label: 'Review', isReview: true },
];

/** Every configured field (steps + welcome gift block), for the conditional-field passes below. */
const ALL_FIELDS: ProfileStepField[] = [
  ...PROFILE_STEPS.flatMap((step) => step.fields || []),
  ...WELCOME_GIFT_FIELDS,
];

/** Required profile fields + banking proof, derived from the step config (mirrors the backend set). */
export const PROFILE_REQUIRED_TOTAL = PROFILE_STEPS.reduce(
  (count, step) =>
    count +
    (step.fields || []).filter((field) => field.required && !field.accountScope && !field.showWhen)
      .length +
    (step.withUpload ? 1 : 0),
  0
);

export const getProfileProgress = (profile: IArtistProfile) => {
  // conditional fields (gift address, state, tax id) count only while the server requires them
  const conditional = ALL_FIELDS.filter(
    (field) => field.required && field.showWhen && field.showWhen(profile)
  ).length;
  const total = PROFILE_REQUIRED_TOTAL + conditional;
  return { done: Math.max(total - profile.missingFields.length, 0), total };
};

export const isStepComplete = (step: ProfileStep, profile: IArtistProfile): boolean => {
  if (step.isReview) return profile.completed;
  const missing = new Set(profile.missingFields);
  // the server lists conditional fields only while they are required
  const fields = [...(step.fields || []), ...(step.withWelcomeGift ? WELCOME_GIFT_FIELDS : [])];
  const fieldsDone = fields.every((field) => !field.required || !missing.has(field.key));
  return fieldsDone && (!step.withUpload || !missing.has('bankingProof'));
};

/** Index of the first step with a missing required field (falls back to the review step). */
export const getFirstIncompleteStep = (profile: IArtistProfile): number => {
  const index = PROFILE_STEPS.findIndex((step) => !step.isReview && !isStepComplete(step, profile));
  return index === -1 ? PROFILE_STEPS.length - 1 : index;
};

export const getProfileDisplayName = (profile: IArtistProfile): string =>
  profile.artistName || profile.fullName || '';

export interface ReviewRow {
  key: ProfileFieldKey | 'bankingProof';
  label: string;
  value: string | null | undefined;
  /** Optional fields show a plain dash instead of a red "missing" when empty. */
  optional?: boolean;
  /** Masked to the last 4 characters, with an eye toggle to reveal. */
  sensitive?: boolean;
}

export interface ReviewGroup {
  title: string;
  stepIndex: number;
  rows: ReviewRow[];
}

/** Every field of every step (required and optional), derived from the step config. */
export const getReviewGroups = (profile: IArtistProfile, accountEmail?: string): ReviewGroup[] =>
  PROFILE_STEPS.filter((step) => !step.isReview).map((step) => {
    const rows: ReviewRow[] = [];
    const giftFields: ProfileStepField[] = step.withWelcomeGift
      ? [{ key: 'welcomeGift', label: 'Welcome gift' }, ...GIFT_ADDRESS_FIELDS, TAX_NUMBER_FIELD]
      : [];
    [...(step.fields || []), ...giftFields]
      .filter((field) => isFieldVisible(field, profile))
      .forEach((field) => {
        const stored = field.accountScope ? accountEmail : profile[field.key as ProfileScalarKey];
        rows.push({
          key: field.key,
          label: field.label,
          value: field.date && stored ? dayjs(stored).format('MM/DD/YYYY') : stored,
          optional: !field.required,
          sensitive: field.sensitive,
        });
      });
    if (step.withUpload) {
      rows.push({
        key: 'bankingProof',
        label: 'Banking Proof for bank account details',
        value: profile.bankingProof?.fileName,
      });
    }
    return { title: step.section ?? step.label, stepIndex: PROFILE_STEPS.indexOf(step), rows };
  });
